import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config/api';
import { getAuthToken, setAuthToken, setRefreshToken } from '../utils/apiAuth';

/**
 * Arrivée depuis Synapse (`/synapse/open?launch=<id>` posé par le serveur Biblio).
 *  - `#pickup=` présent : session native préparée pour un compte déjà attesté → retrait unique,
 *    jeton stocké comme après un login, ouverture du projet ;
 *  - sinon : l'utilisateur se connecte avec SON compte Biblio, puis le compte est attesté.
 * Le fragment est retiré de l'URL dès sa lecture.
 */
export function readLaunchFromLocation(location) {
  const params = new URLSearchParams(location.search);
  const pickup = new URLSearchParams(location.hash.replace(/^#/, '')).get('pickup');
  // Le secret de retrait ne doit pas survivre dans l'historique du navigateur.
  if (pickup && typeof window !== 'undefined') window.history.replaceState(null, '', `${location.pathname}${location.search}`);
  return { launchId: params.get('launch'), pickup };
}

export default function SynapseOpen({ fetcher = fetch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ status: 'working' });
  const launch = useRef(null);
  if (!launch.current) launch.current = readLaunchFromLocation(location);
  const attempted = useRef(false);
  useEffect(() => {
    const current = launch.current;
    if (attempted.current) return;
    const base = buildApiUrl(`synapse/suite/launches/${encodeURIComponent(current.launchId || '')}`);
    const finish = (data) => {
      const projectId = data.nativeSpace && data.nativeSpace.nativeRef ? data.nativeSpace.nativeRef.nativeProjectId : null;
      setState({ status: 'done', projectName: data.project.name, projectId });
      if (projectId) navigate(`/synapse/projects/${encodeURIComponent(projectId)}?launch=${encodeURIComponent(current.launchId)}`, { replace: true });
    };
    const run = async () => {
      attempted.current = true;
      if (!current.launchId || current.launchId === 'error') { setState({ status: 'error', message: 'Ouverture refusée par Synapse : code invalide, expiré ou déjà utilisé.' }); return; }
      try {
        if (current.pickup) {
          const response = await fetcher(`${base}/pickup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pickup: current.pickup }) });
          if (!response.ok) throw new Error('pickup');
          const data = await response.json();
          if (!data.session || !data.session.accessToken) throw new Error('session');
          setAuthToken(data.session.accessToken);
          setRefreshToken(data.session.refreshToken || null);
          finish(data); return;
        }
        const token = getAuthToken();
        if (!token) {
          const status = await fetcher(base);
          if (!status.ok) throw new Error('status');
          const info = await status.json();
          attempted.current = false;
          setState({ status: 'awaiting_native_login', projectName: info.projectName }); return;
        }
        const attested = await fetcher(`${base}/attest`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'X-API-Key': token } });
        if (attested.status === 409) { setState({ status: 'error', message: 'Ce compte Biblio diffère du compte déjà rattaché à votre compte Synapse.' }); return; }
        if (!attested.ok) throw new Error('attest');
        finish(await attested.json());
      } catch (_) { setState({ status: 'error', message: 'L’ouverture depuis Synapse n’a pas abouti. Relancez « Ouvrir » depuis Synapse.' }); }
    };
    run();
  }, [fetcher, navigate]);
  return (
    <main style={{ maxWidth: 560, margin: '0 auto', padding: 24 }} aria-live="polite">
      <h1>Ouverture depuis Synapse</h1>
      {state.status === 'working' && <p role="status">Vérification de l’ouverture…</p>}
      {state.status === 'awaiting_native_login' && (
        <div>
          <p>Connectez-vous avec votre compte Biblio-Tech pour rejoindre « {state.projectName} ». Ce compte sera rattaché à votre compte Synapse.</p>
          <Link to="/">Se connecter</Link>
        </div>
      )}
      {state.status === 'done' && <p role="status">{state.projectId ? `Projet « ${state.projectName} » ouvert.` : `Compte Biblio rattaché à Synapse pour « ${state.projectName} ». Préparez l’espace depuis Synapse.`}</p>}
      {state.status === 'error' && <p role="alert">{state.message}</p>}
    </main>
  );
}
